// Sugalarm — kiosk Wi-Fi connector bridge wrapper (cycle 224).
//
// The single owner of access to the native `KioskWifi` plugin. That plugin
// exists ONLY in the Android kiosk APK (the `kiosk` product flavor); it is
// absent on web, the Pi kiosk, iPhone, the phone layout, and the `standard`
// Android flavor — so `isAvailable()` is false everywhere except the kiosk
// APK, and the Settings → Wi-Fi "Change Wi-Fi network" button gates on it
// (graceful degradation: hidden/disabled with a note when absent).
//
// `openConnector()` opens the EXISTING cycle-223 device-owner Wi-Fi
// connector overlay ON DEMAND — immediately, regardless of online/offline
// state and with NO maintenance PIN. The connector stays join-only (no
// Settings, no exit, never `stopLockTask()`); the Wi-Fi password goes
// straight to `WifiManager` natively and never crosses this bridge. This
// wrapper only triggers the launch — it passes no credentials and reads no
// SSID.
//
// Cycle 473: `capability()` reads the APK's Wi-Fi readiness probe — six
// booleans/enum keys, NEVER an SSID, BSSID or location — so Settings → Wi-Fi
// can say "Ready" / "Not ready (why)" before a tablet leaves the house. An
// APK older than cycle 473 has no `capability` method: the call resolves
// null and the tab shows nothing new (never an error). `wifiReadiness()` is
// the pure rule, the same rule and the same reason order the server applies
// to the heartbeat's copy of this object (`server/schemas/kiosk.py`).
//
// Reached through the bridge-injected `window.Capacitor.Plugins` proxy
// (cycle 26/138 rule) — NEVER a bare `import("@capacitor/...")`. Mirrors the
// `lib/screenBrightness.ts` pattern.

import { isCapacitorNative } from "./capacitor";

export type WifiJoinPath =
  | "device_owner"
  | "add_networks_dialog"
  | "suggestion"
  | "system_panel"
  | "none";

export const WIFI_JOIN_PATHS: readonly WifiJoinPath[] = [
  "device_owner",
  "add_networks_dialog",
  "suggestion",
  "system_panel",
  "none",
];

/** The native probe (`WifiCapability.java`): exactly these six keys. */
export interface WifiCapability {
  device_owner: boolean;
  perms_ok: boolean;
  location_on: boolean;
  wifi_on: boolean;
  can_scan: boolean;
  join_path: WifiJoinPath;
}

interface KioskWifiProxy {
  openConnector(): Promise<void>;
  /** Absent on an APK older than cycle 473. */
  capability?: () => Promise<unknown>;
}

function plugin(): KioskWifiProxy | null {
  if (!isCapacitorNative()) return null;
  const cap = (
    window as { Capacitor?: { Plugins?: Record<string, unknown> } }
  ).Capacitor;
  const p = cap?.Plugins?.KioskWifi;
  return p != null ? (p as KioskWifiProxy) : null;
}

/** True iff the native KioskWifi plugin is registered on the bridge — i.e.
 *  the SPA is running inside the Android kiosk APK (cycle-224 build or
 *  later). The Settings → Wi-Fi "Change Wi-Fi network" button renders
 *  enabled only when this is true. */
export function isAvailable(): boolean {
  return plugin() != null;
}

/** Open the native Wi-Fi connector overlay on demand (no PIN). No-op (and
 *  never throws) when the plugin isn't present. */
export async function openConnector(): Promise<void> {
  const p = plugin();
  if (!p) return;
  try {
    await p.openConnector();
  } catch {
    // The native side never rejects in practice; swallow so the UI never
    // shows a thrown error from the launch bridge.
  }
}

/** Strict parse of the bridge object: the six keys with the right types,
 *  nothing else read. Anything off-shape is null, never a partial object. */
export function parseCapability(raw: unknown): WifiCapability | null {
  if (raw == null || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const bools = ["device_owner", "perms_ok", "location_on", "wifi_on", "can_scan"] as const;
  for (const k of bools) if (typeof o[k] !== "boolean") return null;
  const jp = o.join_path;
  if (typeof jp !== "string" || !(WIFI_JOIN_PATHS as readonly string[]).includes(jp)) {
    return null;
  }
  return {
    device_owner: o.device_owner as boolean,
    perms_ok: o.perms_ok as boolean,
    location_on: o.location_on as boolean,
    wifi_on: o.wifi_on as boolean,
    can_scan: o.can_scan as boolean,
    join_path: jp as WifiJoinPath,
  };
}

/** The APK's readiness probe, or null when there is no bridge, the APK
 *  predates cycle 473 (no `capability` method), the call rejects, or the
 *  answer is off-shape. Never throws. */
export async function capability(): Promise<WifiCapability | null> {
  const p = plugin();
  if (!p || typeof p.capability !== "function") return null;
  try {
    return parseCapability(await p.capability());
  } catch {
    return null;
  }
}

// ─── Readiness (pure) ──────────────────────────────────────────────────

export type WifiReadinessReason =
  | "wifi_off"
  | "permission_missing"
  | "location_off"
  | "no_join_path";

export type WifiReadiness =
  | { ready: true; reason: null }
  | { ready: false; reason: WifiReadinessReason };

/** Ready = the radio is on, the scan grants are held, location is on and
 *  the tablet has SOME way to save a network. The first missing piece is
 *  the reason, in the order the operator fixes them — the same order the
 *  server uses for the admin console, so the tablet and the roster agree. */
export function wifiReadiness(c: WifiCapability): WifiReadiness {
  if (!c.wifi_on) return { ready: false, reason: "wifi_off" };
  if (!c.perms_ok) return { ready: false, reason: "permission_missing" };
  if (!c.location_on) return { ready: false, reason: "location_off" };
  if (c.join_path === "none") return { ready: false, reason: "no_join_path" };
  return { ready: true, reason: null };
}

/** One line per reason. "Sugar Alarm" is the product name in new copy. */
export function readinessCopy(reason: WifiReadinessReason): string {
  switch (reason) {
    case "wifi_off":
      return "Wi-Fi is off on this tablet";
    case "permission_missing":
      return "Location permission missing; set it for Sugar Alarm in the Knox profile";
    case "location_off":
      return "Location is off; turn it on in the Knox profile";
    case "no_join_path":
      return "This tablet has no way to save a new network";
  }
}
